import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { storage } from '../utils/storage';
import { useI18n } from '../i18n';
import { computeSeekTarget, hasSeekableRange, isSeekBarInteractive, readSeekableEnd, resolveSeekDuration } from '../utils/videoSeekTarget';

// ─── Time Formatter ───
export const formatVideoTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ─── Volume Control (horizontal slider) ───
export const VolumeControl: React.FC<{
  volume: number;
  onVolumeChange: (v: number) => void;
}> = ({ volume, onVolumeChange }) => {
  const [hovered, setHovered] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const getVolumeFromEvent = (e: MouseEvent) => {
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return volume;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return Math.round(x * 100) / 100;
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onMove = (e: MouseEvent) => { if (draggingRef.current) onVolumeChange(getVolumeFromEvent(e)); };
    const onUp = () => { draggingRef.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  });

  const isMuted = volume < 0.01;
  const iconName = isMuted ? 'volume-mute' : volume < 0.5 ? 'volume-low' : 'volume-high';

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 4 } as any}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); draggingRef.current = false; }}
    >
      <button
        onClick={() => onVolumeChange(isMuted ? (storage.getVolume() || 0.5) : 0)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          color: 'white', display: 'flex', alignItems: 'center',
        } as any}
      >
        <Ionicons name={iconName as any} size={20} color="white" />
      </button>
      <div
        style={{
          width: hovered ? 80 : 0, overflow: 'hidden',
          transition: 'width 0.2s ease',
          display: 'flex', alignItems: 'center',
        } as any}
      >
        <div
          ref={sliderRef as any}
          style={{
            width: 72, height: 4, backgroundColor: 'rgba(255,255,255,0.25)',
            borderRadius: 2, position: 'relative', cursor: 'pointer', margin: '0 4px',
          } as any}
          onMouseDown={(e: any) => { draggingRef.current = true; onVolumeChange(getVolumeFromEvent(e.nativeEvent)); }}
        >
          <div style={{
            position: 'absolute', left: 0, top: 0, height: '100%',
            width: `${volume * 100}%`, backgroundColor: 'white', borderRadius: 2,
          } as any} />
          <div style={{
            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
            left: `calc(${volume * 100}% - 5px)`,
            width: 10, height: 10, borderRadius: 5, backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          } as any} />
        </div>
      </div>
    </div>
  );
};

// ─── Full Custom Video Controls (play, seek, volume, fullscreen) ───
export const CustomVideoControls: React.FC<{
  videoRef: React.RefObject<HTMLVideoElement | null>;
  volume: number;
  onVolumeChange: (v: number) => void;
  /**
   * CORE-1121 — the practice's real length in seconds, from the API record. Used
   * to scale the seek bar when the element plays (byte ranges work, `seekable` is
   * [0, ∞)) but never learned a finite `duration` — the Yandex.Browser / RU-prod
   * state. Omit for streams with no known length (e.g. live), where the bar falls
   * back to duration / seekable as before.
   */
  knownDurationSec?: number | null;
}> = ({ videoRef, volume, onVolumeChange, knownDurationSec }) => {
  const { t } = useI18n('practices');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [visible, setVisible] = useState(true);
  const [seekDragging, setSeekDragging] = useState(false);
  const seekRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekDraggingRef = useRef(false);
  const playPromiseRef = useRef<Promise<void> | null>(null);

  // Keep ref in sync for use in stable callbacks
  seekDraggingRef.current = seekDragging;

  const resetHideTimer = useCallback(() => {
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => { if (!seekDraggingRef.current) setVisible(false); }, 3000);
  }, []);

  // Video event listeners — stable deps, never re-attached
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const syncPlaying = () => setIsPlaying(!video.paused);
    const onPlay = () => { setIsPlaying(true); };
    const onPlaying = () => { setIsPlaying(true); setIsBuffering(false); };
    const onPause = () => { setIsPlaying(false); setVisible(true); if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDurationChange = () => setDuration(video.duration);
    const onLoadedMetadata = () => setDuration(video.duration);
    const onCanPlay = () => setIsReady(true);
    const onWaiting = () => setIsBuffering(true);
    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);
    if (video.duration) setDuration(video.duration);
    // readyState >= 3 means HAVE_FUTURE_DATA (enough to play)
    if (video.readyState >= 3) setIsReady(true);
    syncPlaying();
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
    };
  }, [videoRef]);

  // CORE-936 — `video.duration` is `Infinity` for an HLS playlist without a
  // declared length and for a stream served without byte-range support, so the
  // old `ratio * duration` produced a non-finite target and every drag threw on
  // the `currentTime` write. `seekable` still bounds those streams; when even
  // that is unknown there is no target and the caller must skip the write.
  const getSeekFromEvent = useCallback((e: MouseEvent): number | null => {
    const rect = seekRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const video = videoRef.current;
    return computeSeekTarget({
      clientX: e.clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      duration,
      seekableEnd: readSeekableEnd(video?.seekable),
      // CORE-1121 — let a click seek by the practice's real length when the
      // element is seekable but never learned a finite duration.
      knownDuration: knownDurationSec ?? null,
      seekablePresent: hasSeekableRange(video?.seekable),
    });
  }, [duration, videoRef, knownDurationSec]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (seekDragging && videoRef.current) {
        const target = getSeekFromEvent(e);
        if (target !== null) setCurrentTime(target);
      }
    };
    const onUp = (e: MouseEvent) => {
      if (seekDragging && videoRef.current) {
        const target = getSeekFromEvent(e);
        if (target !== null) videoRef.current.currentTime = target;
        setSeekDragging(false);
        resetHideTimer();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [seekDragging, videoRef, getSeekFromEvent, resetHideTimer]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isReady) return;
    if (video.paused) {
      const p = video.play();
      if (p) {
        playPromiseRef.current = p;
        p.then(() => { playPromiseRef.current = null; })
         .catch(() => { playPromiseRef.current = null; });
      }
    } else {
      // Wait for any pending play() to resolve before pausing
      const pending = playPromiseRef.current;
      if (pending) {
        pending.then(() => { video.pause(); }).catch(() => {});
      } else {
        video.pause();
      }
    }
    resetHideTimer();
  }, [videoRef, resetHideTimer, isReady]);

  const showSpinner = !isReady || isBuffering;

  const toggleFullscreen = () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else container.requestFullscreen?.();
  };

  // Same duration the seek handler scales by, so the knob and the label stay
  // truthful on streams whose `duration` is `Infinity` (CORE-936). CORE-1121 adds
  // the practice's known length as a last-resort scale when the element is
  // seekable but never learned a duration (Yandex.Browser / RU prod).
  const seekableEnd = readSeekableEnd(videoRef.current?.seekable);
  const seekScale = { knownDuration: knownDurationSec ?? null, seekablePresent: hasSeekableRange(videoRef.current?.seekable) };
  const seekDuration = resolveSeekDuration(duration, seekableEnd, seekScale);
  const progress = seekDuration !== null ? Math.min(100, (currentTime / seekDuration) * 100) : 0;

  // CORE-1121 — with no finite duration, no seekable end, and no known length,
  // every drag is a no-op. Once the video is otherwise playable, present the bar
  // as disabled instead of a working-looking slider that ignores clicks. When the
  // element IS seekable and the API length is known, the bar is live again.
  const canSeek = isSeekBarInteractive(duration, seekableEnd, seekScale);
  const seekDisabled = isReady && !canSeek;

  return (
    <>
    {/* Centered loading spinner */}
    {showSpinner && (
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 15, pointerEvents: 'none',
      } as any}>
        <div style={{
          width: 48, height: 48, borderRadius: 24,
          border: '3px solid rgba(255,255,255,0.15)',
          borderTopColor: '#E98245',
          animation: 'spin 0.8s linear infinite',
        } as any} />
        <style dangerouslySetInnerHTML={{ __html: '@keyframes spin { to { transform: rotate(360deg); } }' }} />
      </div>
    )}
    {/* Full-area hover detection — passes clicks through to video */}
    <div
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 19,
        pointerEvents: 'auto',
        cursor: visible ? 'default' : 'none',
      } as any}
      onMouseMove={(e: any) => { resetHideTimer(); e.stopPropagation(); }}
      onMouseEnter={() => setVisible(true)}
      onClick={(e: any) => {
        // Pass click through to the video element for play/pause toggle
        const video = videoRef.current;
        if (!video || !isReady) return;
        // Don't toggle if clicking on the controls bar area (bottom 60px)
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        if (clickY > rect.height - 60 && visible) return;
        if (video.paused) {
          const p = video.play();
          if (p) {
            playPromiseRef.current = p;
            p.then(() => { playPromiseRef.current = null; }).catch(() => { playPromiseRef.current = null; });
          }
        } else {
          const pending = playPromiseRef.current;
          if (pending) pending.then(() => { video.pause(); }).catch(() => {});
          else video.pause();
        }
        resetHideTimer();
      }}
    />
    <div
      style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20,
        opacity: visible || !isPlaying ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: visible || !isPlaying ? 'auto' : 'none',
      } as any}
    >
      {/* Gradient backdrop */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
        pointerEvents: 'none',
      } as any} />

      {/* Seek bar */}
      <div style={{ position: 'relative', padding: '0 12px', marginBottom: 2 } as any}>
        <div
          ref={seekRef as any}
          data-testid="practice-detail-progress"
          title={seekDisabled ? t('detail.seekUnavailable') : undefined}
          style={{
            width: '100%', height: 4, backgroundColor: 'rgba(255,255,255,0.25)',
            borderRadius: 2, cursor: seekDisabled ? 'not-allowed' : 'pointer',
            opacity: seekDisabled ? 0.4 : 1,
            position: 'relative',
          } as any}
          onMouseDown={(e: any) => {
            // CORE-1121 — no finite seek target on this stream; don't start a
            // drag that can only be a no-op.
            if (!canSeek) return;
            setSeekDragging(true);
            const target = getSeekFromEvent(e.nativeEvent);
            if (target !== null) setCurrentTime(target);
          }}
        >
          <div style={{
            position: 'absolute', left: 0, top: 0, height: '100%',
            width: `${progress}%`, backgroundColor: '#E98245', borderRadius: 2,
          } as any} />
          {!seekDisabled && (
            <div style={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              left: `calc(${progress}% - 6px)`,
              width: 12, height: 12, borderRadius: 6, backgroundColor: '#E98245',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            } as any} />
          )}
        </div>
      </div>

      {/* Controls row */}
      <div style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        padding: '4px 8px 8px', gap: 4,
      } as any}>
        <button
          data-testid={isPlaying ? 'practice-detail-pause' : 'practice-detail-play'}
          onClick={togglePlay}
          disabled={!isReady}
          style={{
            background: 'none', border: 'none',
            cursor: isReady ? 'pointer' : 'default',
            padding: 4, display: 'flex', alignItems: 'center',
            opacity: isReady ? 1 : 0.4,
          } as any}
        >
          <Ionicons name={isPlaying ? 'pause' : 'play'} size={22} color="white" />
        </button>

        <VolumeControl volume={volume} onVolumeChange={onVolumeChange} />

        <span
          data-testid="practice-detail-timecode"
          style={{
          color: 'rgba(255,255,255,0.8)', fontSize: 12, fontFamily: 'monospace',
          marginLeft: 4, userSelect: 'none',
        } as any}>
          {formatVideoTime(currentTime)} / {formatVideoTime(seekDuration ?? duration)}
        </span>

        <div style={{ flex: 1 } as any} />

        <button
          data-testid="practice-detail-fullscreen"
          onClick={toggleFullscreen}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            display: 'flex', alignItems: 'center',
          } as any}
        >
          <Ionicons name="expand" size={20} color="white" />
        </button>
      </div>
    </div>
    </>
  );
};

// ─── Live Stream Controls (volume + fullscreen only, no seek) ───
export const LiveStreamControls: React.FC<{
  volume: number;
  onVolumeChange: (v: number) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ volume, onVolumeChange, containerRef }) => {
  const [visible, setVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetHideTimer = useCallback(() => {
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setVisible(false), 3000);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else containerRef.current.requestFullscreen?.();
  };

  return (
    <div
      style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 30,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      } as any}
      onMouseMove={resetHideTimer}
      onMouseEnter={() => setVisible(true)}
    >
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
        pointerEvents: 'none',
      } as any} />

      <div style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '8px 12px', gap: 4,
      } as any}>
        <VolumeControl volume={volume} onVolumeChange={onVolumeChange} />

        <button
          onClick={toggleFullscreen}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            display: 'flex', alignItems: 'center',
          } as any}
        >
          <Ionicons name="expand" size={20} color="white" />
        </button>
      </div>
    </div>
  );
};
